import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQuery } from '@app/store/baseQuery';
import serverRoutes from '@app/utils/server-routes';
import {
  MonitoringFormTaskControllers,
  MonitoringFormTaskMutationType,
  MonitoringFormTaskObjectType,
  MonitoringFormTasksBulkRequestType,
  MonitoringFormTasksBulkResponseType,
  MonitoringFormTasksStatusesType,
  MonitoringRecordDataType,
  MonitoringRecordEditObjectType,
  MonitoringTasksRecordsRequestType,
  MonitoringTasksRecordsResponseType,
} from '@app/interfaces/qm-module/qm-manage/qm-monioring-tasks.type';

export const qmManageMonitoringTasksApi = createApi({
  reducerPath: 'qmManageMonitoringTasksApi',
  tagTypes: ['qmFTasksList', 'qmFTask', 'controllersList', 'monitoring_record'],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    getMFTasksBulk: build.query<
      MonitoringFormTasksBulkResponseType,
      MonitoringFormTasksBulkRequestType
    >({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_bulk,
        params,
      }),
      providesTags: ['qmFTasksList'],
    }),
    getMFTasksSingle: build.query<MonitoringFormTaskObjectType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks,
        params,
      }),
      providesTags: ['qmFTask'],
    }),
    addMFTask: build.mutation<MonitoringFormTaskObjectType, MonitoringFormTaskMutationType>({
      query: (body) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks,
        body,
        method: 'POST',
      }),
      invalidatesTags: ['qmFTasksList'],
    }),
    editMFTask: build.mutation<
      MonitoringFormTaskObjectType,
      {
        params: {
          id: string;
        };
        body: Partial<MonitoringFormTaskMutationType>;
      }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks,
        params,
        body,
        method: 'PUT',
      }),
      invalidatesTags: ['qmFTask', 'qmFTasksList'],
    }),
    deleteMFTask: build.mutation<MonitoringFormTaskObjectType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks,
        params,
        method: 'DELETE',
      }),
      invalidatesTags: ['qmFTasksList'],
    }),
    cloneMFTask: build.mutation<MonitoringFormTaskObjectType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_clone,
        params,
        method: 'POST',
      }),
      invalidatesTags: ['qmFTasksList'],
    }),
    changeStatusMFTask: build.mutation<
      MonitoringFormTaskObjectType,
      { id: string; status: MonitoringFormTasksStatusesType }
    >({
      query: ({ id, status }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_status,
        params: { id },
        body: { status },
        method: 'PUT',
      }),
      invalidatesTags: ['qmFTasksList', 'qmFTask'],
    }),
    getMFTaskControllers: build.query<MonitoringFormTaskControllers, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_controllers,
        params,
      }),
      providesTags: ['controllersList'],
    }),
    changeMFTaskControllers: build.mutation<
      MonitoringFormTaskControllers,
      { body: MonitoringFormTaskControllers; params: { id: string } }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_controllers,
        params,
        body,
        method: 'PUT',
      }),
      invalidatesTags: ['controllersList'],
    }),
    // MFTasksRecordsBlock
    getRecordsByMFTask: build.query<
      MonitoringTasksRecordsResponseType,
      MonitoringTasksRecordsRequestType
    >({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_records_bulk,
        params,
      }),
    }),
    getRecordMonitoringData: build.query<MonitoringRecordDataType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_record,
        params,
      }),
      providesTags: ['monitoring_record'],
    }),
    getRecordMonitoringDataExport: build.query({
      query: (params: { id: string }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_record_export,
        params,
        responseHandler: (res) => res.blob(),
      }),
    }),
    editRecordMonitoringData: build.mutation<
      MonitoringRecordDataType,
      { params: { id: string }; body: MonitoringRecordEditObjectType }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_tasks_record,
        method: 'PUT',
        params,
        body,
      }),
      invalidatesTags: ['monitoring_record'],
    }),
  }),
});

export const {
  useGetMFTasksBulkQuery,
  useAddMFTaskMutation,
  useChangeStatusMFTaskMutation,
  useGetMFTasksSingleQuery,
  useLazyGetMFTasksSingleQuery,
  useEditMFTaskMutation,
  useDeleteMFTaskMutation,
  useCloneMFTaskMutation,
  // CONGTOLLERS
  useGetMFTaskControllersQuery,
  useChangeMFTaskControllersMutation,
  // MFTasksRecordsBlock
  useGetRecordsByMFTaskQuery,
  useGetRecordMonitoringDataQuery,
  useEditRecordMonitoringDataMutation,
  useLazyGetRecordMonitoringDataExportQuery,
} = qmManageMonitoringTasksApi;
